import React, {createContext, FC, PropsWithChildren, useContext} from 'react';
import styled from 'styled-components';

import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  getSearchIdAttribute,
  searchFocusedAttribute,
  searchIdAttribute,
} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {Highlighter} from '../../../../common/highlighter';
import {useHighlightsCoordinator} from '../../../../common/highlighter/highlightsCoordinator';
import {useConversationSearchContextActivities} from './useConversationSearchContextActivities';

/*
 * Props.
 */

export interface ConversationSearchContextProps {
  query?: string;
  matchesCount: number;
  focusedResultIndex?: number;
  setQuery: (arg: string) => void;
  resetQuery: () => void;
  onResults: (arg: ReadonlyArray<Element>) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  isNumberOfMatchesExact: boolean;
}

/*
 * Context.
 */

const ConversationSearchContext = createContext<ConversationSearchContextProps | null>(null);

export function useConversationSearch() {
  return useContext(ConversationSearchContext);
}

/*
 * Styles.
 */

const StyledConversationHighlighter = styled(Highlighter)`
  background-color: ${(p) => p.theme.palette.orange.shade30};
  color: ${(p) => p.theme.highlightText};

  &[${searchFocusedAttribute}] {
    background-color: ${(p) => p.theme.palette.orange.highlight};
  }
`;

type ConversationSearchContextProviderProps = {
  panelId: ViewerPanelId;
  page: ViewerPanelConversationPageState;
};

/*
 * Component.
 */

export const ConversationSearchContextProvider: FC<
  PropsWithChildren<ConversationSearchContextProviderProps>
> = (props) => {
  const {panelId, page, children} = props;
  const contextValueActivities = useConversationSearchContextActivities({panelId, page});
  const highlightsCoordinator = useHighlightsCoordinator();

  return (
    <ConversationSearchContext.Provider value={contextValueActivities}>
      {contextValueActivities.query && (
        <StyledConversationHighlighter
          query={contextValueActivities.query}
          id="p3-search"
          eachMatch={eachMatch}
          onResults={contextValueActivities.onResults}
          usePartialMatch
          addRefreshCb={highlightsCoordinator?.addRefreshCb}
          removeRefreshCb={highlightsCoordinator?.removeRefreshCb}
        />
      )}
      {children}
    </ConversationSearchContext.Provider>
  );
};

/*
 * Helpers.
 */

/**
 * For each match, try to find the closest searchIdAttribute.
 * If it exists, we're setting it to the element itself.
 */
function eachMatch(element: Element) {
  const closestElementWithSearchId = element.closest(`[${searchIdAttribute}]`);

  if (closestElementWithSearchId) {
    const searchId = getSearchIdAttribute(closestElementWithSearchId);

    if (searchId) {
      element.setAttribute(searchIdAttribute, searchId);
    }
  }
}
